import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';
import { Map, OrderedMap } from 'immutable';

import { DepotHeaderItemEntity } from './depot-header-item.entity';

export interface IDepotHeaderListExtra
	extends IListColumnConfig<DepotHeaderItemEntity> {
	width?: string | number;
}

interface IDepotHeaderListEntity extends IListAttrs<DepotHeaderItemEntity> {
	availableColumns: Map<string, IDepotHeaderListExtra>;
	hasNoSelection: boolean;
}

const entityDefaults: IDepotHeaderListEntity = {
	elements: Map(),
	availableColumns: Map(),
	hasNoSelection: false,
};

export class DepotHeaderListEntity
	implements
		IDepotHeaderListEntity,
		IListOperations<DepotHeaderListEntity, DepotHeaderItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;
	public hasNoSelection = entityDefaults.hasNoSelection;

	public static build(
		availableCols: Array<IDepotHeaderListExtra>
	): DepotHeaderListEntity {
		const inst = new DepotHeaderListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IDepotHeaderListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<DepotHeaderItemEntity>
	> {
		return this.availableColumns;
	}

	public getElements(): Map<string, DepotHeaderItemEntity> {
		return this.elements;
	}

	public setElements(
		elems: Map<string, DepotHeaderItemEntity>
	): DepotHeaderListEntity {
		return produce(this, (draft: DepotHeaderListEntity) => {
			draft.elements = elems;
		});
	}

	public updateElement(
		key: string,
		element: DepotHeaderItemEntity
	): DepotHeaderListEntity {
		const updated = this.elements.set(key, element);
		return produce(this, (draft: DepotHeaderListEntity) => {
			draft.elements = updated;
		});
	}

	public reorder(fromIdx: number, toIdx: number): DepotHeaderListEntity {
		const headerList = this.elements.toList();
		const fromIndex = headerList.findIndex((value) => value.id === fromIdx);
		const toIndex = headerList.findIndex((value) => value.id === toIdx);

		const movedHeader = headerList.get(fromIndex);

		if (!movedHeader) {
			return this;
		}
		let updatedList = headerList.delete(fromIndex);
		updatedList = updatedList.insert(toIndex, movedHeader);

		return produce(this, (draft: DepotHeaderListEntity) => {
			draft.elements = OrderedMap<string, DepotHeaderItemEntity>(
				updatedList.map((value, index) => [
					`${value.id}`,
					value.setPosition(index),
				])
			);
		});
	}

	public updateLabel(entry: number, val: string): DepotHeaderListEntity {
		const elem = this.elements.get(`${entry}`) as DepotHeaderItemEntity;
		const updatedElem = elem.setLabel(val);
		return produce(this, (draft: DepotHeaderListEntity) => {
			draft.elements = draft.elements.set(`${entry}`, updatedElem);
		});
	}

	public updateSelection(
		entry: number,
		checked: boolean
	): DepotHeaderListEntity {
		const elem = this.elements.get(`${entry}`);
		if (!elem) {
			return this;
		}
		const updatedElem = elem.setIsSelected(checked);
		const updated = this.elements.set(`${entry}`, updatedElem);
		return produce(this, (draft: DepotHeaderListEntity) => {
			draft.elements = updated;
			draft.hasNoSelection = this.hasNoneSelected(updated);
		});
	}

	public toggleAllSelection(checked: boolean): DepotHeaderListEntity {
		const updatedElems = this.elements.map((elem) =>
			elem.setIsSelected(checked)
		);
		return produce(this, (draft: DepotHeaderListEntity) => {
			draft.elements = updatedElems;
			draft.hasNoSelection = !checked;
		});
	}

	private hasNoneSelected(
		elements: Map<string, DepotHeaderItemEntity>
	): boolean {
		return elements.every((item: DepotHeaderItemEntity) => !item.isSelected);
	}
}
